/**
 * Frontend publish-grade validation (mirrors backend/workload_pages/validate.py).
 */
import { resolve, isPublishable } from '../registry/builtins';
import { ALLOWED_ACTIONS, RESERVED_ACTIONS } from '../interactions/runActions';

const VISIBILITY = new Set(['private', 'shared_link', 'public']);
const PAGE_ID_RE = /^([a-zA-Z0-9][a-zA-Z0-9_-]{0,127})\/([a-z0-9]+(?:-[a-z0-9]+)*)$/;

function walk(node, visit) {
  if (!node || typeof node !== 'object') return;
  visit(node);
  (node.children || []).forEach((c) => walk(c, visit));
  if (node.fallback) walk(node.fallback, visit);
}

export function manifestCoversRequiredSlots(canvas, manifest) {
  const errors = [];
  const slots = canvas.dataset_slots || [];
  const required = slots.filter(
    (s) => s && (s.required || s.on_missing === 'block_publish'),
  );
  if (!required.length) return errors;
  if (!manifest || typeof manifest !== 'object') {
    errors.push('compatible snapshot manifest is required when canvas has required dataset slots');
    return errors;
  }
  const bySlot = {};
  (manifest.entries || []).forEach((e) => {
    if (e && e.slot) bySlot[e.slot] = e;
  });
  required.forEach((s) => {
    const entry = bySlot[s.id];
    if (!entry) {
      errors.push(`manifest missing required slot '${s.id}'`);
      return;
    }
    if (entry.status === 'failed') {
      errors.push(`manifest slot '${s.id}' is failed; cannot activate canvas`);
    }
  });
  return errors;
}

export function validateCanvasDocument(canvas, { forPublish = false } = {}) {
  const errors = [];
  const warnings = [];
  if (!canvas || typeof canvas !== 'object') {
    return { ok: false, errors: ['canvas must be an object'], warnings };
  }
  if (canvas.schema_version !== 1) errors.push('schema_version must be 1');
  if (!canvas.page_id) errors.push('page_id is required');
  else if (!PAGE_ID_RE.test(canvas.page_id)) {
    errors.push('page_id must be tenant_id/slug');
  }
  if (!canvas.tenant_id) errors.push('tenant_id is required');
  if (canvas.page_id && canvas.tenant_id) {
    const tenant = String(canvas.page_id).split('/')[0];
    if (tenant !== String(canvas.tenant_id)) {
      errors.push('tenant_id does not match page_id tenant segment');
    }
  }
  if (!canvas.title) {
    errors.push(
      'canvas.title is required (document-level string next to schema_version; root.title does not satisfy this)'
    );
  }
  if (!canvas.visibility || !VISIBILITY.has(canvas.visibility.policy)) {
    errors.push('visibility.policy must be private|shared_link|public');
  }
  if (canvas.rows && !canvas.root) {
    errors.push('legacy rows/blocks documents are not valid canvas schema');
  }

  const slotIds = new Set();
  (canvas.dataset_slots || []).forEach((s, i) => {
    if (!s || !s.id) {
      errors.push(`dataset_slots[${i}] requires id`);
      return;
    }
    if (slotIds.has(s.id)) errors.push(`duplicate dataset slot id '${s.id}'`);
    slotIds.add(s.id);
    if (!s.dataset_id) errors.push(`dataset_slots[${i}] requires dataset_id`);
  });

  if (!canvas.root || typeof canvas.root !== 'object') {
    errors.push('root must be a component node object');
  } else {
    const seen = new Set();
    walk(canvas.root, (node) => {
      if (!node.id) errors.push('every node requires a string id');
      else if (seen.has(node.id)) errors.push(`duplicate node id '${node.id}'`);
      else seen.add(node.id);

      ['runtime', 'permissions', 'bundle'].forEach((f) => {
        if (f in node) {
          errors.push(`node '${node.id}' must not set ${f}; registry owns that field`);
        }
      });

      const version = node.version || '1.0.0';
      const defn = resolve(node.component, version);
      if (!defn) {
        errors.push(`node '${node.id}': unknown component ${node.component}@${version}`);
        return;
      }
      const hasFallback = !!(node.fallback && typeof node.fallback === 'object');
      if (forPublish) {
        const pub = isPublishable(node.component, version, { required: !hasFallback });
        if (!pub.ok) errors.push(`node '${node.id}': ${pub.message}`);
        else if (hasFallback && !defn.runtimeImplemented) {
          warnings.push(`node '${node.id}': unimplemented component will use fallback`);
        }
      }
      if (node.children && node.children.length && !defn.acceptsChildren) {
        errors.push(`node '${node.id}': ${node.component} does not accept children`);
      }
      const bindings = node.bindings || {};
      Object.keys(bindings).forEach((prop) => {
        const b = bindings[prop];
        if (!b || typeof b !== 'object') {
          errors.push(`node '${node.id}': bindings.${prop} must be an object`);
          return;
        }
        const slot = b.dataset || b.slot;
        if (!slot) errors.push(`node '${node.id}': bindings.${prop} requires dataset/slot`);
        else if (!slotIds.has(slot)) {
          errors.push(`node '${node.id}': bindings.${prop} references unknown slot '${slot}'`);
        }
        if (b.path == null) errors.push(`node '${node.id}': bindings.${prop} requires path`);
      });
      const events = node.events || {};
      Object.keys(events).forEach((ev) => {
        (events[ev] || []).forEach((action, i) => {
          if (!action || !action.action) return;
          if (RESERVED_ACTIONS.has(action.action)) {
            errors.push(`node[${node.id}].events.${ev}[${i}]: action '${action.action}' is reserved`);
          } else if (!ALLOWED_ACTIONS.has(action.action)) {
            errors.push(`node[${node.id}].events.${ev}[${i}]: unknown action '${action.action}'`);
          }
        });
      });
    });
  }

  return { ok: errors.length === 0, errors, warnings };
}

export function validateForPublish(canvas, manifest) {
  const result = validateCanvasDocument(canvas, { forPublish: true });
  manifestCoversRequiredSlots(canvas, manifest).forEach((e) => result.errors.push(e));
  result.ok = result.errors.length === 0;
  return result;
}
